import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { ClientSelectionService } from '../components/clients/client-selection.service';
import { RecoveryAgentsResult } from '../models/recovery-agents.model';
import { RecoveryAgent } from '../models/recoveryagent.model';

@Injectable({
  providedIn: 'root',
})
export class RecoveryAgentsService {
  private readonly endpoint = 'recovery-agents/agents';

  constructor(
    private http: HttpClient,
    private clientSelectionService: ClientSelectionService
  ) {}

  getRecoveryAgents(
    skip = 0,
    count = 10,
    sortBy?: string,
    sortDir?: 'asc' | 'desc',
    searchTerm?: string
  ): Observable<RecoveryAgentsResult> {
    const params = new HttpParams()
      .set('skip', skip.toString())
      .set('count', count.toString())
      .set('sortBy', sortBy ?? '')
      .set('sortDir', sortDir ?? '')
      .set('search', searchTerm?.trim() ?? '');

    return this.http
      .get<any>(this.getUrl(), {
        headers: this.getHeaders(),
        params,
      })
      .pipe(
        map((response) => {
          const recoveryAgents = this.extractRecoveryAgents(response).map((item) =>
            this.normalizeRecoveryAgent(item)
          );

          return {
            recoveryAgents,
            totalCount: this.extractTotalCount(response, recoveryAgents.length),
          };
        })
      );
  }

  createRecoveryAgent(payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl(), this.buildCreatePayload(payload), {
      headers: this.getHeaders(),
    });
  }

  updateRecoveryAgent(payload: RecoveryAgent): Observable<any> {
    return this.http.put<any>(this.getUrl(), this.buildUpdatePayload(payload), {
      headers: this.getHeaders(),
    });
  }

  getOperatingCompanies(): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}operatingcompanies`, {
      headers: this.getHeaders(),
    });
  }

  getOffices(): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}offices`, {
      headers: this.getHeaders(),
    });
  }

  getRecoveryAgentStates(): Observable<any> {
    return this.http.get<any>(`${this.getUrl()}/states`, {
      headers: this.getHeaders(),
    });
  }

  deleteRecoveryAgent(recoveryAgentId: number, lastUpdatedBy: string): Observable<any> {
    return this.http.delete<any>(`${this.getUrl()}/${recoveryAgentId}`, {
      headers: this.getHeaders(),
      body: {
        LastUpdatedBy: String(lastUpdatedBy ?? '').trim(),
      },
    });
  }

  private getUrl(): string {
    return `${environment.apiUrl}${this.endpoint}`;
  }

  private getHeaders(): HttpHeaders {
    const clientDbName = this.clientSelectionService.getSelectedClientDbName();

    return new HttpHeaders({
      ClientDBName: clientDbName,
    });
  }

  private extractRecoveryAgents(response: any): RecoveryAgent[] {
    if (Array.isArray(response?.recoveryAgents)) {
      return response.recoveryAgents;
    }

    if (Array.isArray(response?.RecoveryAgents)) {
      return response.RecoveryAgents;
    }

    if (Array.isArray(response?.agents)) {
      return response.agents;
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    if (Array.isArray(response?.items)) {
      return response.items;
    }

    return [];
  }

  private normalizeRecoveryAgent(item: any): RecoveryAgent {
    return {
      RAMailingName: this.readValue(item, ['RAMailingName', 'raMailingName']),
      Street1: this.readValue(item, ['Street1', 'street1']),
      Street2: this.readValue(item, ['Street2', 'street2']),
      City: this.readValue(item, ['City', 'city']),
      State: this.readValue(item, ['State', 'state']),
      ZipCode: this.readValue(item, ['ZipCode', 'zipCode']),
      ZipPlus4: this.readValue(item, ['ZipPlus4', 'zipPlus4']),
      PaperlessInd: this.readValue(item, ['PaperlessInd', 'paperlessInd']),
      LoadDate: this.readValue(item, ['LoadDate', 'loadDate']),
      LastUpdate: this.readValue(item, ['LastUpdate', 'lastUpdate']),
      RecoveryAgentTaxID: this.readValue(item, [
        'RecoveryAgentTaxID',
        'recoveryAgentTaxID',
        'TaxID',
        'taxId',
      ]),
      ExternalOrg: this.readValue(item, ['ExternalOrg', 'externalOrg']),
      OperatingCompany: this.readValue(item, [
        'OperatingCompany',
        'operatingCompany',
      ]),
      OfficeKey: this.readValue(item, ['OfficeKey', 'officeKey']),
      LastUpdatedBy: this.readValue(item, ['LastUpdatedBy', 'lastUpdatedBy']),
      RecoveryAgentID: this.toRequiredNumber(
        this.readValue(item, ['RecoveryAgentID', 'recoveryAgentID'], 0),
        0
      ),
      OpCompanyID: this.toNullableNumber(
        this.readValue(item, ['OpCompanyID', 'opCompanyID'], null)
      ),
      ClaimOfficeID: this.toNullableNumber(
        this.readValue(item, ['ClaimOfficeID', 'claimOfficeID'], null)
      ),
    };
  }

  private readValue(source: any, keys: string[], defaultValue: any = ''): any {
    for (const key of keys) {
      if (source?.[key] !== undefined && source?.[key] !== null) {
        return source[key];
      }
    }

    return defaultValue;
  }

  private buildUpdatePayload(payload: RecoveryAgent): RecoveryAgent {
    const requestPayload = this.omitComputedFields(payload);

    return {
      ...requestPayload,
      RecoveryAgentID: this.toRequiredNumber(requestPayload.RecoveryAgentID, 0),
      OpCompanyID: this.toNullableNumber(requestPayload.OpCompanyID),
      ClaimOfficeID: this.toNullableNumber(requestPayload.ClaimOfficeID),
    };
  }

  private buildCreatePayload(payload: any): any {
    return this.omitComputedFields(payload);
  }

  private omitComputedFields(payload: any): any {
    if (!payload || typeof payload !== 'object') {
      return payload;
    }

    const { LoadDate, LastUpdate, ...requestPayload } = payload;
    return requestPayload;
  }

  private toRequiredNumber(value: unknown, fallback = 0): number {
    const converted = Number(value);
    return Number.isFinite(converted) ? converted : fallback;
  }

  private toNullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const converted = Number(value);
    return Number.isFinite(converted) ? converted : null;
  }

  private extractTotalCount(response: any, fallbackCount: number): number {
    const directCount =
      response?.totalCount ??
      response?.TotalCount ??
      response?.count ??
      response?.Count ??
      response?.totalRecords ??
      response?.TotalRecords;

    if (typeof directCount === 'number') {
      return directCount;
    }

    const nestedCount =
      response?.pagination?.totalCount ??
      response?.pagination?.TotalCount ??
      response?.meta?.totalCount ??
      response?.meta?.TotalCount;

    if (typeof nestedCount === 'number') {
      return nestedCount;
    }

    return fallbackCount;
  }
}
